/**
 * The discovery toolbar: search plus refresh, with an optional trailing control.
 *
 * Home and the full Explore page ask for the same two controls over the same catalogue, so the
 * box and the refresh button live in one component rather than as two inline copies that can
 * drift apart. The caller still owns the query state and any extra action to the right.
 */
import type { ReactNode } from 'react';
import { RefreshCw, Search } from 'lucide-react';

import IconButton from './IconButton';
import { c } from '../../styles/theme';

export interface DiscoveryToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  refreshing: boolean;
  onRefresh: () => void;
  trailingAction?: ReactNode;
  placeholder?: string;
  searchLabel?: string;
  refreshLabel?: string;
  marginBottom?: number;
}

export default function DiscoveryToolbar({
  query,
  onQueryChange,
  refreshing,
  onRefresh,
  trailingAction,
  placeholder = 'Search discoverable music by title, genre, mood or artist',
  searchLabel = 'Search discoverable music',
  refreshLabel = 'Refresh discoverable music',
  marginBottom = 18,
}: DiscoveryToolbarProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom }}>
      <div
        className="lyx-discovery-search"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flex: 1,
          minWidth: 0,
          height: 40,
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          padding: '0 12px',
          background: c.surface1,
        }}
      >
        <Search size={15} color={c.text3} style={{ flexShrink: 0 }} aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={placeholder}
          aria-label={searchLabel}
          style={{
            border: 'none',
            background: 'transparent',
            color: c.text1,
            fontSize: 14,
            width: '100%',
          }}
        />
      </div>
      <IconButton
        label={refreshing ? refreshLabel.replace(/^Refresh/, 'Refreshing') : refreshLabel}
        onClick={onRefresh}
        disabled={refreshing}
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          background: c.surface1,
        }}
      >
        <RefreshCw
          size={15}
          color={c.text2}
          aria-hidden="true"
          className={refreshing ? 'lyx-spin' : undefined}
        />
      </IconButton>
      {trailingAction}
    </div>
  );
}
